import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { makeRequest, axiosOrgInstance, axiosDotNetOrgInstance, type ApiEnvelope } from './middleware';
import { sortLocationTree } from '@/lib/location-order';
import type {
    AdminOrganizationAccessRequest,
    AdminOrganizationByIdData,
    AdminOrganizationData,
    AdminServiceLevelOption,
    AdminOrganizationUserData,
    AdminOrganizationVisitorData,
    Role,
    Toast,
    ToastType,
    User,
    ServiceDesk,
    AdminOrgInfoBody,
    AdminOrganizationDefaultGroup,
    AdminOrganizationFormData,
    AdminServiceDeskInfo,
    AdminSLAType,
    AdminSLABody,
    AdminContract,
    AdminUserInvitation,
    DotNetUserInvitationDto,
    AdminUserEstablished,
    AdminUserImpersonationRequest,
    AdminSubjectAccessToken,
    AdminOrgByUserData,
    AdminUserById,
    AdminContact,
    ContactType,
    AdminContactFormData,
    UUIDLocation,
    OrganizationData,
    VisitorById,
    VisitorByOrgId,
    SimpleUser,
    OrganizationType,
    Group,
    RequestType,
    AdminAccessCard,
} from './store-interfaces';

import { jwtDecode } from 'jwt-decode';
import { getCPRolesFromToken, getOrganizationSchemeRolesFromToken } from './helpers';
import { tokenVersion } from './token-signal';
import type { AdminUserCreationBody } from '@/views/admin/user/user-creation-view.vue';
import type { VisitorCreationBody } from '@/views/visitors/create-visitor-view.vue';
import type { UserDetailsUpdatePayload } from './organization';

type DotNetResponseEnvelope<T> = {
    statusCode: number;
    message?: string;
    detail?: string;
    data?: T;
};

type SubmitUserImpersonationRequestPayload = {
    userId: string;
    description: string;
    startDate: string;
    endDate: string;
    action: 0 | 1;
};

type AdminGroupAccessDetails = {
    group: Group;
    roles: Role[];
    request_types: RequestType[];
    incident_request_types: RequestType[];
    locations: unknown[];
};

type AdminUserOrgAccessDetails = {
    groups: (Group & { access_details?: AdminGroupAccessDetails })[];
    direct_roles: Role[];
    effective_roles: Role[];
    role_request_types: RequestType[];
    effective_request_types: RequestType[];
    effective_incident_request_types: RequestType[];
    effective_locations: unknown[];
};

export const useAdminStore = defineStore('admin', () => {
    // constants
    const adminsOrganizations = ref<AdminOrganizationData[]>([]);
    const usersOrganization = ref<OrganizationData>();
    const authOrganization = ref<AdminOrganizationData>();
    const chosenOrganization = ref<AdminOrganizationByIdData>();
    const userCPRoles = ref<string[]>([]);
    const organizationSchemeRoles = ref<string[]>([]);
    const user = ref<User>();
    const toastMessage = ref<Toast[]>([]);
    const token = localStorage.getItem('token');
    const tokenExpires = localStorage.getItem('tokenExpires');

    startup();

    const canApprove = computed(() => {
        return userCPRoles.value.includes('admin') || userCPRoles.value.includes('noc-admin');
    });
    const effectiveUserCPRoles = computed(() =>
        userCPRoles.value.length > 0 ? userCPRoles.value : getCPRolesFromToken(localStorage.getItem('token') ?? ''),
    );
    const effectiveOrganizationSchemeRoles = computed(() =>
        organizationSchemeRoles.value.length > 0
            ? organizationSchemeRoles.value
            : getOrganizationSchemeRolesFromToken(localStorage.getItem('token') ?? ''),
    );
    const adminRoleContext = computed(() => ({
        userCPRoles: effectiveUserCPRoles.value,
        organizationSchemeRoles: effectiveOrganizationSchemeRoles.value,
    }));

    // localStorage is not reactive: without this, roles derived at store creation stay
    // stale after an in-SPA login or token refresh, and the admin router guard keeps
    // bouncing to /loading. The auth store bumps tokenVersion on every token write.
    watch(tokenVersion, () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        userCPRoles.value = getCPRolesFromToken(token);
        organizationSchemeRoles.value = getOrganizationSchemeRolesFromToken(token);
    });

    async function startup() {
        if (
            token === null ||
            tokenExpires === null ||
            new Date(Number(localStorage.getItem('tokenExpires')) * 1000).getTime() < Date.now()
        )
            return;
        userCPRoles.value = getCPRolesFromToken(token);
        organizationSchemeRoles.value = getOrganizationSchemeRolesFromToken(token);
        if (userCPRoles.value.includes('admin')) {
            await populateAdminOrganizations();
            await populateUser();
        }
    }

    async function populateAdminOrganizations() {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationData[]>>(
            'GET',
            'admin-management/organizations',
            axiosOrgInstance,
        );

        if (adminsOrganizations.value.length === 0 && response.data) {
            adminsOrganizations.value = response.data.data;
            usersOrganization.value = adminsOrganizations.value[0] as unknown as OrganizationData;
        } else authOrganization.value = adminsOrganizations.value[0];
    }

    /**
     * Status corresponds to creation process status
     * @param status 0 = pending, 1 = active, undefined = all
     * @returns
     */
    async function getOrganizationsByStatus(status: 0 | 1 | undefined) {
        const reqString =
            status !== undefined
                ? `admin-management/organizations?created=${status}`
                : `admin-management/organizations`;
        const response = await makeRequest<ApiEnvelope<AdminOrganizationData[]>>('GET', reqString, axiosOrgInstance);
        return response.data.data;
    }

    async function getAllOrganizations() {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationData[]>>(
            'GET',
            'admin-management/organizations',
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getAdminAccessCards() {
        const response = await makeRequest<DotNetResponseEnvelope<AdminAccessCard[]>>(
            'GET',
            'admin/access-cards',
            axiosDotNetOrgInstance,
            undefined,
            { throwOnError: true },
        );
        return response.data.data ?? [];
    }

    async function removeAdminAccessCards(accessCardIds: string[]) {
        await makeRequest(
            'DELETE',
            'admin/access-cards',
            axiosDotNetOrgInstance,
            { accessCardIds },
            { throwOnError: true },
        );
    }

    async function chooseOrganizationById(id: number) {
        chosenOrganization.value = undefined;
        chosenOrganization.value = await getOrganizationById(id);
        return chosenOrganization.value;
    }

    async function getOrganizationsByUserId(userId: string) {
        const response = await makeRequest<ApiEnvelope<AdminOrgByUserData[]>>(
            'GET',
            `admin-management/users/${userId}/organizations`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function populateUser() {
        if (token !== null) {
            const decodedToken = jwtDecode(token) as { sub: string };
            user.value = await getUser(decodedToken.sub);
        } else {
            console.error('No token found');
        }
    }

    async function getUser(userId: string) {
        const response = await makeRequest<ApiEnvelope<User>>(
            'GET',
            `admin-management/users/${userId}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getUserById(userId: string) {
        const response = await makeRequest<ApiEnvelope<AdminUserById>>(
            'GET',
            `admin-management/users/${userId}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getOrganizationById(id: number) {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationByIdData>>(
            'GET',
            `admin-management/organizations/${id}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getUsersByOrgId(id: number) {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationUserData[]>>(
            'GET',
            `admin-management/organizations/${id}/users`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getAllUsers() {
        const response = await makeRequest<ApiEnvelope<AdminUserEstablished[]>>(
            'GET',
            'admin-management/users',
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getOrganizationRoles() {
        const response = await makeRequest<ApiEnvelope<Role[]>>(
            'GET',
            'admin-management/organizationroles',
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getOrganizationsByRoleId(roleId: number) {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationData[]>>(
            'GET',
            `admin-management/organizationroles/${roleId}/organizations`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getOrganizationRole(id: number) {
        const response = await makeRequest<ApiEnvelope<Role>>(
            'GET',
            `admin-management/organizationroles/${id}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function addOrganizationToRole(orgIds: number[], roleId: number) {
        const body = orgIds.map((value) => ({ organization_id: value }));
        const response = await makeRequest(
            'PUT',
            `admin-management/organizationroles/${roleId}/organizations`,
            axiosOrgInstance,
            body,
        );
        return (response.status === 204 || response.status === 200) as boolean;
    }

    async function removeRolesFromOrganization(orgId: number, roleIds: number[]) {
        const body = roleIds.map((value) => ({ organization_role_id: value }));
        const response = await makeRequest<ApiEnvelope>(
            'DELETE',
            `admin-management/organizations/${orgId}/roles`,
            axiosOrgInstance,
            body,
        );
        return response.data.success;
    }

    async function deleteUserFromOrganization(orgId: number, userId: string) {
        const body = [
            {
                organization_id: orgId,
            },
        ];
        const response = await makeRequest<ApiEnvelope>(
            'DELETE',
            `admin-management/users/${userId}/organizations`,
            axiosOrgInstance,
            body,
        );
        return response.data.success;
    }

    async function getRolesByOrgId(id: number) {
        const response = await makeRequest<ApiEnvelope<Role[]>>(
            'GET',
            `admin-management/organizations/${id}/roles`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getUserGroupsByOrgId(orgId: number, userId: string) {
        const response = await makeRequest<ApiEnvelope<Group[]>>(
            'GET',
            `organization-management/organizations/${orgId}/users/${userId}/groups`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getUserRolesByOrgId(orgId: number, userId: string) {
        const response = await makeRequest<ApiEnvelope<Role[]>>(
            'GET',
            `organization-management/organizations/${orgId}/users/${userId}/roles`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getRequestTypesByOrgId(orgId: number) {
        const response = await makeRequest<ApiEnvelope<RequestType[]>>(
            'GET',
            `organization-management/organizations/${orgId}/requesttypes`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getGroupAccessDetailsByOrgId(orgId: number, groupId: number) {
        const response = await makeRequest<ApiEnvelope<AdminGroupAccessDetails>>(
            'GET',
            `admin-management/organizations/${orgId}/groups/${groupId}/access-details`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getUserAccessDetailsByOrgId(orgId: number, userId: string) {
        const response = await makeRequest<ApiEnvelope<AdminUserOrgAccessDetails>>(
            'GET',
            `admin-management/organizations/${orgId}/users/${userId}/access-details`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getVisitorsByOrgId(id: number) {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationVisitorData[]>>(
            'GET',
            `admin-management/organizations/${id}/visitors`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getAccessRequestsByOrgId(id: number) {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationAccessRequest[]>>(
            'GET',
            `admin-management/organizations/${id}/siteaccessrequests`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getServiceLevelOptions() {
        const response = await makeRequest<ApiEnvelope<AdminServiceLevelOption[]>>(
            'GET',
            `admin-management/serviceLevels`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getServiceDeskOptions() {
        const response = await makeRequest<ApiEnvelope<AdminServiceDeskInfo[]>>(
            'GET',
            `admin-management/servicedesks`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getServiceDesksByOrgId(id: number) {
        const response = await makeRequest<ApiEnvelope<ServiceDesk[]>>(
            'GET',
            `admin-management/organizations/${id}/servicedesks`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getAllRoles() {
        const response = await makeRequest<ApiEnvelope<Role[]>>(
            'GET',
            `admin-management/organizationroles`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getAllGroupsByOrgId(id: number) {
        const response = await makeRequest<
            ApiEnvelope<{ selected: AdminOrganizationDefaultGroup[]; unselected: AdminOrganizationDefaultGroup[] }>
        >('GET', `admin-management/organizations/${id}/defaultgroups`, axiosOrgInstance);
        return response.data.data;
    }

    function setToastMessage(message: string, type: ToastType) {
        const index = toastMessage.value.length;
        toastMessage.value?.push({ message, type, index });
    }

    function clearToastMessage() {
        toastMessage.value.shift();
    }

    async function startCreationProcess(body: AdminOrgInfoBody) {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationFormData>>(
            'PUT',
            'admin-management/organizations',
            axiosOrgInstance,
            body,
        );

        return response.data.data;
    }

    async function submitPoolStep(id: number, body: Array<{ organization_role_id: number }>) {
        const response = await makeRequest<ApiEnvelope>(
            'PUT',
            `admin-management/organizations/${id}/roles`,
            axiosOrgInstance,
            body,
        );

        return response.data.success;
    }

    async function submitGroupStep(id: number, body: Array<{ default_group_id: number }>) {
        const response = await makeRequest<ApiEnvelope>(
            'PUT',
            `admin-management/organizations/forms/${id}/defaultgroups`,
            axiosOrgInstance,
            body,
        );

        return response.data.success;
    }

    async function submitSummaryStepToReview(formId: number) {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationFormData>>(
            'PUT',
            `admin-management/organizations/forms/${formId}/review`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function submitSummaryStep(formId: number) {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationFormData>>(
            'PUT',
            `admin-management/organizations/forms/${formId}/approve`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getAllInProcessForms() {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationFormData[]>>(
            'GET',
            `admin-management/organizations/forms`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getFormDataByFormId(id: number) {
        const response = await makeRequest<ApiEnvelope<AdminOrganizationFormData>>(
            'GET',
            `admin-management/organizations/forms/${id}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getLocationsByOrgId(uuid: string) {
        const response = await makeRequest<ApiEnvelope<UUIDLocation[]>>(
            'GET',
            `admin/organizations/${uuid}/locations`,
            axiosDotNetOrgInstance,
        );
        return sortLocationTree(response.data.data);
    }

    async function getAllLocations() {
        const response = await makeRequest<ApiEnvelope<UUIDLocation[]>>(
            'GET',
            `admin/locations`,
            axiosDotNetOrgInstance,
        );

        return sortLocationTree(response.data.data);
    }

    async function getUsersByLocationId(id: string, includeNotification?: boolean) {
        const response = await makeRequest<ApiEnvelope<SimpleUser[]>>(
            'GET',
            `admin/locations/${id}/users${includeNotification ? '?notifications=true' : ''}`,
            axiosDotNetOrgInstance,
        );
        return response.data.data;
    }

    async function getAllSLATypes() {
        const response = await makeRequest<ApiEnvelope<AdminSLAType[]>>(
            'GET',
            'admin-management/sla/types',
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getContractDataByOrgId(id: number) {
        const response = await makeRequest<ApiEnvelope<AdminContract[]>>(
            'GET',
            `admin-management/organizations/${id}/contracts`,
            axiosOrgInstance,
        );
        return response.data.data[0];
    }

    async function submitContractStep(formId: number, body: AdminSLABody) {
        const response = await makeRequest<ApiEnvelope>(
            'PUT',
            `admin-management/organizations/forms/${formId}/contract`,
            axiosOrgInstance,
            body,
        );

        return response.data.success;
    }

    async function getEstablishedUsers() {
        const response = await makeRequest<ApiEnvelope<AdminUserEstablished[]>>(
            'GET',
            'admin-management/users',
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function submitImpersonationRequest(body: SubmitUserImpersonationRequestPayload) {
        const response = await makeRequest<DotNetResponseEnvelope<AdminUserImpersonationRequest>>(
            'POST',
            'admin/users/impersonations',
            axiosDotNetOrgInstance,
            body,
            {
                throwOnError: true,
                skipToast: true,
            },
        );
        return response.data;
    }

    async function getUserImpersonationRequests(userId: string) {
        const response = await makeRequest<DotNetResponseEnvelope<AdminUserImpersonationRequest[]>>(
            'GET',
            `admin/users/${userId}/impersonations`,
            axiosDotNetOrgInstance,
            undefined,
            {
                throwOnError: true,
                skipToast: true,
            },
        );
        return response.data.data ?? [];
    }

    async function getSubjectAccessToken(userId: string) {
        const response = await makeRequest<DotNetResponseEnvelope<AdminSubjectAccessToken>>(
            'POST',
            `admin/users/${userId}/subject-access-token`,
            axiosDotNetOrgInstance,
            undefined,
            {
                throwOnError: true,
                skipToast: true,
            },
        );

        if (!response.data.data) {
            throw new Error(response.data.detail || response.data.message || 'Failed to start impersonation.');
        }

        return response.data.data;
    }

    function mapAdminUserInvitation(dto: DotNetUserInvitationDto): AdminUserInvitation {
        return {
            user_invitation_id: dto.userInvitationId,
            email: dto.email ?? '',
            first_name: dto.firstName ?? '',
            last_name: dto.lastName ?? '',
            organization:
                dto.organizationId !== null && dto.organizationName !== null
                    ? { organization_id: dto.organizationId, name: dto.organizationName }
                    : null,
            status: dto.status,
            created_at: dto.createdAt,
            modified_at: dto.modifiedAt,
            created_by: dto.createdBy,
            creator: dto.creator
                ? {
                      ID: dto.creator.id,
                      FIRST_NAME: dto.creator.firstName ?? '',
                      LAST_NAME: dto.creator.lastName ?? '',
                      EMAIL: dto.creator.email ?? '',
                      USERNAME: dto.creator.username ?? '',
                      ENABLED: dto.creator.enabled,
                  }
                : null,
            groups: dto.groups ?? [],
            roles: dto.roles ?? [],
        };
    }

    async function getUserInvitations() {
        const response = await makeRequest<ApiEnvelope<DotNetUserInvitationDto[]>>(
            'GET',
            `admin/users/invitations`,
            axiosDotNetOrgInstance,
        );
        const invitations = response.data.data ?? [];
        return invitations.map(mapAdminUserInvitation);
    }

    async function approveUserInvitation(invitationId: number) {
        const response = await makeRequest(
            'POST',
            `admin/users/invitations/${invitationId}/approve`,
            axiosDotNetOrgInstance,
        );
        return response.data;
    }

    async function declineUserInvitation(invitationId: number, comment?: string) {
        const response = await makeRequest(
            'DELETE',
            `admin/users/invitations/${invitationId}`,
            axiosDotNetOrgInstance,
            comment ? { comment } : undefined,
            {
                throwOnError: true,
                skipToast: true,
            },
        );
        return response.data;
    }

    async function createUser(body: AdminUserCreationBody) {
        const response = await makeRequest<ApiEnvelope>('POST', `admin-management/users`, axiosOrgInstance, [body]);
        return response.data;
    }

    async function getAllContacts() {
        const response = await makeRequest<ApiEnvelope<AdminContact[]>>(
            'GET',
            `admin-management/contacts`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getAllContactTypes(ids: string | undefined) {
        const response = await makeRequest<ApiEnvelope<ContactType[]>>(
            'GET',
            `admin-management/contacts/types${ids ? `?organizationIds=${ids}` : ''}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function removeContactFromOrg(orgId: number, contactIds: number[]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const body: any[] = [];
        contactIds.forEach((contactId) => {
            body.push({
                organization_contact_id: contactId,
            });
        });
        const response = await makeRequest(
            'DELETE',
            `admin-management/organizations/${orgId}/contacts`,
            axiosOrgInstance,
            body,
        );
        return response;
    }

    async function assignContacts(body: AdminContactFormData[]) {
        const response = await makeRequest<ApiEnvelope>('POST', `admin-management/contacts`, axiosOrgInstance, body);
        return response.data.success;
    }

    async function getCompletedCoursesByUsedId(userId: string) {
        const response = await makeRequest<ApiEnvelope<unknown[]>>(
            'GET',
            `admin-management/users/${userId}/courses`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function getLocationsByUserId(userId: string) {
        const response = await makeRequest<ApiEnvelope<UUIDLocation[]>>(
            'GET',
            `admin-management/users/${userId}/locations`,
            axiosOrgInstance,
        );
        return sortLocationTree(response.data.data);
    }

    async function getVisitors(securityStatus: null | 0 | 1, organizations?: number[]) {
        const security = securityStatus !== null ? 'securityStatus=' + securityStatus : '';
        const orgs = organizations ? 'organizations=' + organizations.join(',') : '';
        const response = await makeRequest<ApiEnvelope<VisitorByOrgId[]>>(
            'GET',
            `admin-management/visitors?${[security, orgs].join('&')}`,
            axiosOrgInstance,
        );
        // Type name could be changed to something else
        return response.data.data;
    }

    async function getVisitorById(id: string) {
        const response = await makeRequest<ApiEnvelope<VisitorById>>(
            'GET',
            `admin-management/visitors/${id}`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    async function postVisitor(body: VisitorCreationBody): Promise<boolean> {
        const response = await makeRequest('POST', `admin-management/visitors`, axiosOrgInstance, [body]);
        return response.status === 201;
    }

    async function deleteVisitor(id: string, body: VisitorCreationBody['organizations']): Promise<boolean> {
        const response = await makeRequest(
            'DELETE',
            `admin-management/visitors/${id}/organizations`,
            axiosOrgInstance,
            body,
        );
        return response.status === 204;
    }

    async function putOrganizationsToVisitor(id: string, body: VisitorCreationBody['organizations']): Promise<boolean> {
        const response = await makeRequest(
            'PUT',
            `admin-management/visitors/${id}/organizations`,
            axiosOrgInstance,
            body,
        );
        return response.status === 201;
    }

    async function patchVisitor(
        id: string,
        body: Omit<VisitorCreationBody, 'email' | 'organizations' | 'company'> & { security_briefed?: boolean },
    ): Promise<boolean> {
        const response = await makeRequest('PATCH', `admin-management/visitors/${id}`, axiosOrgInstance, body);
        return response.status === 200;
    }

    /** Company is edited per (organization, visitor) connection; an empty string clears it. */
    async function patchVisitorOrganizationCompany(id: string, organizationId: number, company: string): Promise<boolean> {
        const response = await makeRequest(
            'PATCH',
            `admin-management/visitors/${id}/organizations/${organizationId}`,
            axiosOrgInstance,
            { company },
        );
        return response.status === 204;
    }

    async function updateUserDetails(userId: string, body: UserDetailsUpdatePayload) {
        const response = await makeRequest<DotNetResponseEnvelope<unknown>>(
            'PATCH',
            `/admin/users/${userId}`,
            axiosDotNetOrgInstance,
            body,
            { throwOnError: true, skipToast: true },
        );
        return response.data?.data;
    }

    async function setUserSecurityBriefingCompletion(userId: string, completed: boolean): Promise<boolean> {
        const response = await makeRequest(
            'PUT',
            `admin-management/users/${userId}/courses/security-briefing`,
            axiosOrgInstance,
            { completed },
        );
        return response.status === 204;
    }

    async function getOrganizationTypes() {
        const response = await makeRequest<ApiEnvelope<OrganizationType[]>>(
            'GET',
            `admin-management/organizations/types`,
            axiosOrgInstance,
        );
        return response.data.data;
    }

    return {
        adminsOrganizations,
        authOrganization,
        usersOrganization,
        chosenOrganization,
        user,
        userCPRoles,
        organizationSchemeRoles,
        adminRoleContext,
        toastMessage,
        canApprove,

        populateAdminOrganizations,
        chooseOrganizationById,
        populateUser,
        getUserById,
        getOrganizationsByStatus,
        getAllOrganizations,
        getAdminAccessCards,
        removeAdminAccessCards,
        getOrganizationById,
        getOrganizationsByUserId,
        getUsersByOrgId,
        getAllUsers,
        getRolesByOrgId,
        getUserGroupsByOrgId,
        getUserRolesByOrgId,
        getRequestTypesByOrgId,
        getGroupAccessDetailsByOrgId,
        getUserAccessDetailsByOrgId,
        getVisitorsByOrgId,
        getAccessRequestsByOrgId,
        getServiceLevelOptions,
        getServiceDeskOptions,
        getServiceDesksByOrgId,
        getAllRoles,
        getAllGroupsByOrgId,
        setToastMessage,
        clearToastMessage,
        startCreationProcess,
        submitPoolStep,
        submitGroupStep,
        getFormDataByFormId,
        getLocationsByOrgId,
        getAllInProcessForms,
        getAllLocations,
        getAllSLATypes,
        getContractDataByOrgId,
        submitContractStep,
        submitSummaryStepToReview,
        submitSummaryStep,
        getEstablishedUsers,
        submitImpersonationRequest,
        getUserImpersonationRequests,
        getSubjectAccessToken,
        getUserInvitations,
        approveUserInvitation,
        declineUserInvitation,

        createUser,
        getAllContacts,
        getAllContactTypes,
        assignContacts,
        removeContactFromOrg,
        getCompletedCoursesByUsedId,
        getLocationsByUserId,
        getVisitors,
        getVisitorById,
        postVisitor,
        deleteVisitor,
        putOrganizationsToVisitor,
        patchVisitor,
        patchVisitorOrganizationCompany,
        getUsersByLocationId,
        getOrganizationTypes,
        getOrganizationRoles,
        getOrganizationsByRoleId,
        getOrganizationRole,
        addOrganizationToRole,
        removeRolesFromOrganization,
        deleteUserFromOrganization,
        updateUserDetails,
        setUserSecurityBriefingCompletion,
    };
});
